import {useColorScheme, StyleSheet, TextStyle, ViewStyle, View, Text, Pressable, GestureResponderEvent} from 'react-native';
import {theme} from 'config/themes';
import {fonts} from 'config/styles';
import {isAndroid} from 'utils/platform';

import type {ReactNode} from 'react';

export interface IconButtonProps {
  text?: string;
  mode?: 'primary' | 'cancel' | 'destructive';
  icon?: ReactNode;
  iconHover?: ReactNode;
  title?: string;
  disabled?: boolean;
  onPress?: (e: GestureResponderEvent) => void;
  iconStyle?: ViewStyle | ViewStyle[];
  textStyle?: TextStyle | TextStyle[];
  textHoverStyle?: TextStyle | TextStyle[];
  buttonStyle?: ViewStyle | ViewStyle[];
  buttonHoverStyle?: ViewStyle | ViewStyle[];
  reference?: any,
}

export function IconButton(props: IconButtonProps) {
  const scheme = useColorScheme();
  return (
    <Pressable
      ref={props.reference}
      accessibilityRole="button"
      accessibilityLabel={props.title}
      onPress={props.onPress ?? undefined}
      disabled={props.disabled}
      android_ripple={{
        borderless: !props.mode && !props.text,
        color: scheme === 'dark'
          ? 'rgba(255, 255, 255, 0.25)'
          : 'rgba(0, 0, 0, 0.25)'
      }}
      /* @ts-ignore */
      style={({pressed, hovered, focused}) => {
        const isHighlighted: boolean = !isAndroid() && (pressed || hovered || focused);
        const isDestructive = props.mode === 'destructive';
        const isPrimary = props.mode === 'primary';
        const isCancel = props.mode === 'cancel';
        return [
          styles.button,
          props.buttonStyle,
          isHighlighted && props.buttonHoverStyle,
          isPrimary && styles.buttonPrimary,
          isPrimary && isHighlighted && styles.buttonPrimaryHover,
          isDestructive && styles.buttonDestructive,
          isDestructive && isHighlighted && styles.buttonDestructiveHover,
          isCancel && styles.buttonCancel,
          isCancel && isHighlighted && styles.buttonCancelHover,
          props.disabled && styles.disabled,
        ];
      }}
      /* @ts-ignore */>
      {({pressed, hovered, focused}) => {
        const isHighlighted: boolean = !isAndroid() && (pressed || hovered || focused);
        const isDestructive = props.mode === 'destructive';
        const isPrimary = props.mode === 'primary';
        const isCancel = props.mode === 'cancel';
        const hasIcon = !!props.icon;
        const hasText = !!props.text;
        return (
          <View style={styles.inner}>
            {hasIcon &&
              <View style={[styles.icon, props.iconStyle]}>
                {isHighlighted && props.iconHover ? props.iconHover : props.icon}
              </View>
            }
            {hasIcon && hasText &&
              <View style={styles.spacer}/>
            }
            {hasText &&
              <Text style={[
                styles.text,
                props.textStyle,
                isPrimary && styles.textPrimary,
                isDestructive && styles.textDestructive,
                isCancel && styles.textCancel,
                isHighlighted && props.textHoverStyle,
              ]}>
                {props.text}
              </Text>
            }
          </View>
        );
      }}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    padding: 1,
  },
  disabled: {
    opacity: 0.2,
  },
  icon: {
    width: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spacer: {
    width: 10,
  },
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    padding: 1,
    minWidth: 90,
    minHeight: 40,
    maxHeight: 40,
    borderWidth: 2,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: 'transparent',
  },
  buttonPrimary: {
    borderColor: theme.button.primary.border,
    backgroundColor: theme.button.primary.fill,
  },
  buttonPrimaryHover: {
    backgroundColor: theme.button.primary.hover,
  },
  buttonCancel: {
    borderColor: theme.button.cancel.border,
    backgroundColor: theme.button.cancel.fill,
  },
  buttonCancelHover: {
    backgroundColor: theme.button.cancel.hover,
  },
  buttonDestructive: {
    borderColor: theme.button.destructive.border,
    backgroundColor: theme.button.destructive.fill,
  },
  buttonDestructiveHover: {
    backgroundColor: theme.button.destructive.hover,
  },
  text: {
    //...fonts.regular,
    fontSize: fonts.sizes.xs,
    padding: 5,
  },
  textPrimary: {
    color: theme.button.primary.text,
  },
  textCancel: {
    color: theme.button.cancel.text,
  },
  textDestructive: {
    color: theme.button.destructive.text,
  },
});
